/**
 * User-facing errors of the services (`src/services/**`): the http client's defaults, the server codes mapped
 * to our wording, and the per-call messages of `createHttpApi`. Read in the current language when the error
 * is raised (`translate`, `messageMap`).
 */
export const fr = {
  /* http client defaults (by status) */
  'http.network': 'Connexion impossible. Vérifiez votre réseau, puis réessayez.',
  'http.timeout': 'Le serveur met trop de temps à répondre. Réessayez dans un instant.',
  'http.unauthorized': 'Connectez-vous pour continuer.',
  /* The old site's wording (sessionExpiry / login): a session that expired during the visit (CK-02, AU-02) */
  'http.sessionExpired': 'Votre session a expiré. Veuillez vous reconnecter.',
  'http.forbidden': 'Vous n’avez pas accès à cet élément.',
  /* Local testing on real data only (`npm run dev:prod-readonly`, scripts/dev/readOnlyGuard.mjs) */
  'http.readOnly':
    'Mode lecture seule : en local sur les données réelles, cette action est désactivée (rien n’est envoyé).',
  'http.not_found': 'Élément introuvable.',
  'http.unavailable': 'Le service est momentanément indisponible. Réessayez dans un instant.',
  'http.invalid': 'La demande n’a pas pu être traitée. Vérifiez les informations saisies.',
  'http.tooMany': 'Trop de demandes en peu de temps. Patientez un instant, puis réessayez.',
  'http.badResponse': 'Réponse inattendue du serveur. Réessayez dans un instant.',

  /* Auth codes of the backend */
  'auth.INVALID_CREDENTIALS': 'E-mail ou mot de passe incorrect.',
  'auth.ACCOUNT_NOT_FOUND': 'Aucun compte n’existe avec cet e-mail.',
  'auth.GOOGLE_AUTH_ACCOUNT': 'Ce compte utilise la connexion Google : continuez avec Google.',
  'auth.INVALID_GOOGLE_TOKEN': 'La connexion avec Google a échoué : réessayez, ou utilisez votre e-mail.',
  'auth.ACCOUNT_CONFLICT':
    'Un compte Cardnd existe déjà avec ces informations : connectez-vous avec votre e-mail et votre mot de passe.',
  'auth.GOOGLE_SIGNIN_ERROR': 'La connexion avec Google n’a pas abouti. Réessayez dans un instant.',
  'auth.EMAIL_EXISTS': 'Un compte existe déjà avec cet e-mail.',
  'auth.DB_INDEX_CONFLICT': 'L’inscription est momentanément indisponible. Réessayez plus tard.',
  'auth.INVALID_PHONE_NUMBER': 'Numéro de téléphone invalide. Format attendu : 06 12 34 56 78.',
  'auth.INVALID_CLIENT_NAME': 'Le nom doit contenir entre 2 et 80 caractères.',
  'auth.INVALID_DATE_OF_BIRTH': 'Date de naissance invalide.',
  'auth.INVALID_BIO': 'La présentation ne peut pas dépasser 500 caractères.',
  'auth.IMAGE_TOO_LARGE': 'L’image est trop volumineuse (maximum 10 Mo).',
  'auth.UNSUPPORTED_IMAGE_TYPE': 'Format d’image non pris en charge. Utilisez JPG, PNG, WEBP ou HEIC.',
  'auth.UPLOAD_FAILED': 'L’envoi de l’image a échoué. Veuillez réessayer.',
  'auth.googleUnavailable': 'La connexion avec Google est indisponible pour le moment : utilisez votre e-mail.',
  'auth.resetNoCode': 'Demandez d’abord un code depuis « Mot de passe oublié », sur cet appareil.',
  'auth.resetBadCode': 'Code invalide ou expiré : demandez-en un nouveau.',

  /* Coupons (the server answers in English) */
  'coupon.empty': 'Saisissez un code promo.',
  'coupon.notFound': 'Code promo invalide ou expiré.',
  'coupon.limit': 'Ce code promo a atteint sa limite d’utilisation.',

  /* Payment resume codes (PaymentResumeError, RATE_UNAVAILABLE) */
  'resume.ALREADY_PAID': 'Cette réservation est déjà payée : retrouvez-la dans Mes voyages.',
  'resume.VEHICLE_UNAVAILABLE':
    'Ce véhicule n’est plus disponible sur ces dates : choisissez d’autres dates ou un autre modèle.',
  'resume.EXPIRED_CART': 'Cette réservation a expiré : refaites une réservation pour ces dates.',
  'resume.EXPIRED_DATES': 'Les dates de cette réservation sont passées : refaites une réservation.',
  'resume.NOT_RESUMABLE': 'Cette réservation ne peut plus être payée.',
  'resume.NOT_ONLINE': 'Cette réservation ne se paie pas en ligne.',
  'resume.RATE_UNAVAILABLE':
    'Le paiement dans cette devise est momentanément indisponible. Réessayez dans un instant ou payez en MAD.',

  /* Vehicles and quotes */
  vehicleGone: 'Ce véhicule n’est plus disponible.',
  minDays: {
    one: 'Ce véhicule se loue pour {count} jour minimum : allongez la location.',
    other: 'Ce véhicule se loue pour {count} jours minimum : allongez la location.',
  },
  returnBeforeStart: 'La date de retour doit être après la date de départ.',
  'booking.invalid': 'La réservation n’a pas pu être créée. Vérifiez les dates et les informations du conducteur.',
  'booking.taken': 'Ce modèle n’est plus disponible sur ces dates : choisissez d’autres dates.',
  'booking.licence': 'Votre permis doit avoir au moins 2 ans pour réserver.',
  'booking.paymentInit':
    'Le paiement en ligne n’a pas pu être initialisé. Votre demande est conservée : réessayez dans un instant.',
  bookingNotFound: 'Réservation introuvable.',
  bookingSignIn: 'Connectez-vous pour voir cette réservation.',
  tripsSignIn: 'Connectez-vous pour voir vos voyages.',

  /* Experiences */
  experienceGone: 'Cette expérience n’est plus disponible.',
  'slot.noSeatsAt': {
    one: 'Plus assez de places à {time} pour {count} personne : choisissez un autre départ.',
    other: 'Plus assez de places à {time} pour {count} personnes : choisissez un autre départ.',
  },
  'slot.noSeatsDay': {
    one: 'Plus assez de places ce jour-là pour {count} personne : choisissez un autre départ.',
    other: 'Plus assez de places ce jour-là pour {count} personnes : choisissez un autre départ.',
  },
  'slot.noDepartureAt': 'Plus de départ à {time} ce jour-là : choisissez une autre heure.',
  'slot.noDeparture': 'Plus de départ ce jour-là : choisissez une autre date.',
  'experienceBooking.invalid':
    'La réservation n’a pas pu être créée. Vérifiez les participants et les informations demandées.',
  'experienceBooking.slotGone': 'Ce départ n’est plus disponible : choisissez une autre date.',
  'experienceBooking.slotTaken': 'Ce départ n’est plus disponible : choisissez une autre heure ou une autre date.',
  'experienceBooking.details': 'Certaines informations demandées par le prestataire manquent ou ne conviennent pas.',
  'experienceBooking.waiver': 'Acceptez la décharge du prestataire pour réserver.',
  'experienceBooking.account': 'Un compte est nécessaire pour réserver une expérience.',
  'eligibility.signIn': 'Connectez-vous pour demander la gratuité.',
  'eligibility.tooMany': 'Vous avez déjà trop de demandes en attente : patientez la réponse du prestataire.',
  'eligibility.notFound': 'Demande de gratuité introuvable.',
  'tickets.notIssued': 'Les billets du prestataire ne sont pas encore émis.',
  'tickets.wait': 'Patientez une minute avant un nouvel envoi.',
  'tickets.failed': 'Envoi impossible pour le moment, réessayez plus tard.',

  /* Trips */
  cancelClosed: 'Cette réservation ne peut plus être annulée.',
  'cancel.experienceTerms': 'Les expériences suivent les conditions d’annulation du prestataire.',
  'extension.unavailable': 'Le véhicule n’est pas disponible pour les nouvelles dates demandées.',
  'rating.comment': 'Ajoutez un commentaire à votre avis.',
  'rating.alreadyRated': 'Vous avez déjà noté cette expérience.',
  'rating.notYet': 'Vous avez déjà noté cette expérience, ou elle n’a pas encore eu lieu.',
  'voucher.signIn': 'Connectez-vous pour télécharger le bon.',
  'voucher.notReady': 'Le bon de réservation n’est pas encore disponible.',
  'voucher.failed': 'Impossible de télécharger le bon de réservation.',

  /* Account */
  'favourites.signIn': 'Connectez-vous pour voir vos favoris.',
  'favourites.save': 'Connectez-vous pour enregistrer vos favoris.',
  itemGone: 'Cet élément n’est plus disponible.',
  'messages.signIn': 'Connectez-vous pour voir vos messages.',
  'messages.closed': 'Cette conversation est terminée : vous pouvez la consulter, mais plus y répondre.',
  'messages.rated': 'Cette conversation a déjà été évaluée.',
  'messages.botChoice': 'Choisissez une des réponses proposées pour continuer.',
  'reviews.signIn': 'Connectez-vous pour voir vos avis.',
  'reviews.load': 'Impossible de charger vos avis.',
  'reviews.notYours': 'Cet avis ne vous appartient pas.',
  'reviews.gone': 'Cet avis n’existe plus.',

  /* Content, partner, newsletter */
  agencyNotFound: 'Cette agence est introuvable.',
  articleNotFound: 'Cet article est introuvable.',
  'partner.experiences':
    'Les demandes de partenariat pour les expériences ne se font pas encore en ligne : écrivez-nous depuis la page Contact.',
  'partner.signIn': 'Connectez-vous pour envoyer votre demande.',
  'partner.invalid': 'Vérifiez les numéros de téléphone et les champs obligatoires.',
  'partner.exists': 'Une demande existe déjà pour cet e-mail.',
  'partner.failed': 'La demande n’a pas pu être enregistrée. Réessayez dans un instant.',
  'newsletter.email': 'Cette adresse e-mail n’est pas acceptée : vérifiez-la.',
  'contact.missing': 'Remplissez le nom, l’e-mail, le sujet et le message, puis réessayez.',
  'contact.failed': 'Échec de l’envoi du message. Réessayez dans un instant.',
  'contact.network': 'Échec de l’envoi du message : vérifiez votre connexion, puis réessayez.',

  /* Payments */
  'payments.unavailable': 'Le paiement en ligne est momentanément indisponible. Réessayez dans un instant.',
  'payments.finalizing': 'Votre carte est pré-autorisée ; sa prise en compte par Cardnd prend un peu plus de temps.',
  'payments.signIn': 'Connectez-vous pour payer cette réservation.',
  'payments.init':
    'Le paiement en ligne n’a pas pu être initialisé. Votre réservation est conservée : réessayez dans un instant.',
} as const;
